import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bot, ChevronDown, Loader2, Maximize2, Minimize2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Markdown } from '@/components/Markdown';
import { cn } from '@/lib/utils';
import type { AssistantChatState, ChatMessage } from './useAssistantStream';

/**
 * Shared presentation for the AI assistants. `AssistantPanel` is the side-rail card used on
 * record pages (ticket / opportunity detail); `AssistantFab` is the floating button used on
 * list pages. Both render the same `ChatBody`, so the two assistants stay in lockstep.
 */

interface ChatProps {
  chat: AssistantChatState;
  suggestions: readonly string[];
  suggestionsLabel: string;
  placeholder: string;
  /** Rendered under an assistant message (e.g. the Projects AI's proposal cards). */
  renderExtras?: (m: ChatMessage) => React.ReactNode;
}

// ---------------- The chat modal (shared by every assistant) ----------------

type DialogSize = 'normal' | 'large';
const SIZE_KEY = 'cosmos:assistant:size';
const SIZE_CLASS: Record<DialogSize, string> = {
  // Wide enough for a 5-column table without squashing; drag the corner to adjust.
  normal: 'w-[min(60rem,94vw)] h-[min(48rem,85vh)]',
  large: 'w-[96vw] h-[92vh]',
};

/**
 * The pop-out / floating chat window for every AI assistant. Wider than a normal dialog so tables read well,
 * resizable from the bottom-right corner (it stays centred), and a maximise toggle that's remembered.
 */
function AssistantDialog({ open, onOpenChange, title, children }: { open: boolean; onOpenChange: (v: boolean) => void; title: string; children: React.ReactNode }) {
  const [size, setSize] = useState<DialogSize>(() => { try { return (localStorage.getItem(SIZE_KEY) as DialogSize) === 'large' ? 'large' : 'normal'; } catch { return 'normal'; } });
  const ref = useRef<HTMLDivElement>(null);
  const toggle = () => {
    const next: DialogSize = size === 'large' ? 'normal' : 'large';
    // A manual drag-resize leaves inline width/height; clear it so the preset applies.
    if (ref.current) { ref.current.style.width = ''; ref.current.style.height = ''; }
    setSize(next);
    try { localStorage.setItem(SIZE_KEY, next); } catch { /* storage unavailable */ }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={ref}
        className={cn('flex max-h-[96vh] max-w-[98vw] resize flex-col overflow-hidden', 'min-h-[22rem] min-w-[min(26rem,94vw)]', SIZE_CLASS[size])}>
        <DialogHeader className="pr-20">
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" /> {title}
          </DialogTitle>
        </DialogHeader>
        <button type="button" onClick={toggle} title={size === 'large' ? 'Restore size' : 'Maximise'} aria-label={size === 'large' ? 'Restore size' : 'Maximise'}
          className="absolute right-11 top-[0.85rem] rounded-sm p-1 text-muted-foreground opacity-70 hover:bg-accent hover:text-foreground hover:opacity-100">
          {size === 'large' ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
        <div className="flex min-h-0 flex-1 flex-col px-6 pb-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

/** Collapsible card with a pop-out modal — for detail-page side rails. */
export function AssistantPanel({
  title = 'AI Assistant',
  defaultOpen = false,
  ...props
}: ChatProps & { title?: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [popped, setPopped] = useState(false);
  const { pathname } = useLocation();

  // Same reason as the FAB: a deal link in a reply navigates behind the pop-out.
  useEffect(() => { setPopped(false); }, [pathname]);

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground">
        <Bot className="h-4 w-4 shrink-0 text-primary" />
        <span>{title}</span>
        <button
          type="button"
          onClick={() => setPopped(true)}
          title="Pop out"
          className="ml-auto rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title={open ? 'Collapse' : 'Expand'}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      {open && !popped && (
        <div className="border-t border-border px-3 pb-3 pt-2">
          <ChatBody {...props} scrollClass="max-h-80" />
        </div>
      )}
      {open && popped && (
        <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
          Chat is open in the pop-out window.
        </div>
      )}

      <AssistantDialog open={popped} onOpenChange={setPopped} title={title}>
        <ChatBody {...props} scrollClass="flex-1 min-h-0" />
      </AssistantDialog>
    </div>
  );
}

/**
 * Floating action button + modal — for list pages, where there's no side rail to live in.
 * On mobile the button clears the fixed `BottomNav` (h-16) plus the iOS safe-area inset.
 */
export function AssistantFab({
  title = 'AI Assistant',
  label,
  ...props
}: ChatProps & { title?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Clicking a deal link in a reply navigates the page behind the modal — close it so the
  // user lands on the record they asked for instead of staring at the chat.
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={title}
        aria-label={title}
        className={cn(
          'fixed right-4 z-40 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-lg',
          'transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'bottom-[calc(4rem+env(safe-area-inset-bottom)+0.75rem)] md:bottom-6',
          open && 'hidden',
        )}
      >
        <Bot className="h-5 w-5 shrink-0" />
        {label && <span className="hidden text-sm font-medium sm:inline">{label}</span>}
      </button>

      <AssistantDialog open={open} onOpenChange={setOpen} title={title}>
        <ChatBody {...props} scrollClass="flex-1 min-h-0" />
      </AssistantDialog>
    </>
  );
}

export function ChatBody({
  chat, suggestions, suggestionsLabel, placeholder, scrollClass, renderExtras,
}: ChatProps & { scrollClass: string }) {
  const { messages, input, setInput, streaming, error, send } = chat;
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  return (
    <>
      <div ref={scrollRef} className={cn('space-y-2 overflow-y-auto', scrollClass)}>
        {messages.length === 0 && (
          <div className="space-y-2 py-1">
            <p className="px-1 text-xs text-muted-foreground">{suggestionsLabel}</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={cn(
              'rounded-md px-2.5 py-1.5 text-sm',
              m.role === 'user' ? 'bg-primary/10 text-foreground whitespace-pre-wrap break-words' : 'bg-muted/50 text-foreground',
            )}
          >
            {m.role === 'assistant'
              ? (m.content
                  ? <Markdown content={m.content} />
                  : (streaming && i === messages.length - 1 && !m.proposals?.length
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      : null))
              : m.content}
            {m.role === 'assistant' && renderExtras?.(m)}
          </div>
        ))}
      </div>

      {error && <p className="mt-2 px-1 text-xs text-destructive">{error}</p>}

      <form className="mt-2 flex items-end gap-1.5" onSubmit={(e) => { e.preventDefault(); send(input); }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
          placeholder={placeholder}
          rows={1}
          className="min-h-8 max-h-32 flex-1 resize-none rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <Button type="submit" size="sm" disabled={streaming || !input.trim()} className="h-8 px-2.5">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </>
  );
}
